import { useRef, type ReactNode } from 'react';
import type { Components } from 'react-markdown';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Download } from 'lucide-react';
import { cn } from '@/utils/cn';
import { downloadTextFile, rowsToCsv } from '@/api/csvExport';

function cellText(cell: Element): string {
  return (cell.textContent ?? '').replace(/\s+/g, ' ').trim();
}

function extractTableData(table: HTMLTableElement): { headers: string[]; rows: string[][] } {
  const headerCells = table.querySelectorAll('thead th');
  const headers =
    headerCells.length > 0
      ? Array.from(headerCells, cellText)
      : Array.from(table.querySelectorAll('tr:first-child th, tr:first-child td'), cellText);

  const bodyRows = table.querySelectorAll('tbody tr');
  const rowsSource =
    bodyRows.length > 0
      ? bodyRows
      : Array.from(table.querySelectorAll('tr')).slice(headers.length > 0 ? 1 : 0);

  const rows = Array.from(rowsSource, (row) =>
    Array.from(row.querySelectorAll('th, td'), cellText)
  );

  return { headers, rows };
}

function MarkdownTable({ children }: { children?: ReactNode }) {
  const tableRef = useRef<HTMLTableElement>(null);

  const handleDownload = () => {
    const table = tableRef.current;
    if (!table) return;

    const { headers, rows } = extractTableData(table);
    if (headers.length === 0 && rows.length === 0) return;

    const csv = rowsToCsv(headers, rows);
    const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, (c) => (c === 'T' ? '_' : '-'));
    downloadTextFile(`chat-table-${stamp}.csv`, csv);
  };

  return (
    <div className="mb-3 overflow-hidden rounded-xl border border-border last:mb-0">
      <div className="flex items-center justify-end border-b border-border bg-surface px-3 py-2">
        <button
          type="button"
          onClick={handleDownload}
          className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-surface px-2.5 py-1 text-[11px] font-medium text-foreground-muted transition-colors hover:border-brand hover:text-foreground"
          aria-label="Download table as CSV"
        >
          <Download className="h-3.5 w-3.5" />
          Download CSV
        </button>
      </div>
      <div className="overflow-x-auto">
        <table
          ref={tableRef}
          className="w-full min-w-full border-collapse text-left text-xs"
        >
          {children}
        </table>
      </div>
    </div>
  );
}

const markdownComponents: Components = {
  p: ({ children }) => <p className="mb-3 last:mb-0">{children}</p>,
  h1: ({ children }) => (
    <h1 className="mb-3 mt-4 text-base font-semibold first:mt-0">{children}</h1>
  ),
  h2: ({ children }) => (
    <h2 className="mb-2 mt-4 text-sm font-semibold first:mt-0">{children}</h2>
  ),
  h3: ({ children }) => (
    <h3 className="mb-2 mt-3 text-sm font-medium first:mt-0">{children}</h3>
  ),
  ul: ({ children }) => <ul className="mb-3 list-disc space-y-1 pl-5 last:mb-0">{children}</ul>,
  ol: ({ children }) => <ol className="mb-3 list-decimal space-y-1 pl-5 last:mb-0">{children}</ol>,
  li: ({ children }) => <li className="leading-relaxed">{children}</li>,
  strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
  em: ({ children }) => <em className="italic">{children}</em>,
  a: ({ href, children }) => (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="font-medium text-brand underline underline-offset-2 hover:text-brand-hover"
    >
      {children}
    </a>
  ),
  blockquote: ({ children }) => (
    <blockquote className="mb-3 border-l-2 border-border pl-3 text-foreground-muted last:mb-0">
      {children}
    </blockquote>
  ),
  hr: () => <hr className="my-4 border-border" />,
  code: ({ className, children, ...props }) => {
    const isBlock = Boolean(className?.startsWith('language-'));
    if (isBlock) {
      return (
        <code className={cn('font-mono text-xs', className)} {...props}>
          {children}
        </code>
      );
    }
    return (
      <code
        className="rounded bg-surface-muted px-1 py-0.5 font-mono text-[0.85em]"
        {...props}
      >
        {children}
      </code>
    );
  },
  pre: ({ children }) => (
    <pre className="mb-3 overflow-x-auto rounded-xl bg-surface-muted p-3 font-mono text-xs last:mb-0">
      {children}
    </pre>
  ),
  table: ({ children }) => <MarkdownTable>{children}</MarkdownTable>,
  thead: ({ children }) => (
    <thead className="border-b border-border bg-surface-muted/80">{children}</thead>
  ),
  tbody: ({ children }) => <tbody className="divide-y divide-border bg-surface">{children}</tbody>,
  tr: ({ children }) => <tr className="align-top">{children}</tr>,
  th: ({ children }) => (
    <th className="px-3.5 py-2.5 text-[11px] font-semibold tracking-wide text-foreground-muted">
      {children}
    </th>
  ),
  td: ({ children }) => (
    <td className="px-3.5 py-2.5 text-foreground">{children}</td>
  ),
};

type MarkdownContentProps = {
  content: string;
  className?: string;
};

export function MarkdownContent({ content, className }: MarkdownContentProps) {
  return (
    <div className={cn('markdown-content', className)}>
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
        {content}
      </ReactMarkdown>
    </div>
  );
}
